import { useState } from 'react'
import moment from 'moment'
import {
  useBenefitEligibilityTemplates,
  useClubBenefitAssignments,
  useClubBenefitOffers,
  useMarkBenefitRedeemed,
} from '@/services/clubBenefits'
import { serverErrorMessage } from '@/services/httpService'
import { BenefitEligibilityPicker } from './BenefitEligibilityPicker'
import { ClubClaimsExport } from './ClubClaimsExport'
import { CMButton, EmptyState, Spinner } from './ui'

interface ClubBenefitsTabProps {
  clubId: number
}

/**
 * Members who have claimed a benefit at this club.
 *
 * Built to be worked at a counter: a name, a DUPR ID, the code the member is showing, what they
 * are owed, and a way to tick them off. Names arrive already redacted for members who asked for that, so nothing here reconstructs
 * one from other fields.
 *
 * Redemption is advisory — DUPR does not police whether a club honored a benefit, it just records
 * that someone said they did.
 */
export const ClubBenefitsTab = ({ clubId }: ClubBenefitsTabProps) => {
  const {
    data: assignmentPages,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useClubBenefitAssignments(clubId)
  // Read for the eligibility picker below, which renders one rule row per offered benefit. The club
  // no longer changes this set — DUPR operations author it — so it is fetched, never written.
  const { data: offers, isError: offersFailed, refetch: refetchOffers } = useClubBenefitOffers(clubId)
  // Also fetched by the eligibility picker below; React Query shares the one request between them,
  // so reading it here costs nothing.
  const { isError: templatesFailed, refetch: refetchTemplates } = useBenefitEligibilityTemplates(clubId)
  const markRedeemed = useMarkBenefitRedeemed(clubId)
  // Which claim failed to mark, and why. Keyed by grant so one failure cannot mislabel another row.
  const [redeemError, setRedeemError] = useState<{ grantId: number; message: string } | null>(null)

  if (isLoading) return <Spinner />

  const claims = assignmentPages?.pages.flat() ?? []

  const markClaimRedeemed = (grantId: number) => {
    setRedeemError(null)
    markRedeemed.mutate(grantId, {
      // Without this the mutation simply settled, the button re-enabled, and nothing on screen
      // changed — so a 400 or a 401 looked exactly like a slow success, and a front desk would tap
      // "Mark redeemed" indefinitely while the member waited. The server's own sentence is the one
      // that says what to do about it.
      onError: (error) =>
        setRedeemError({
          grantId,
          message: serverErrorMessage(error, 'Could not mark that as redeemed. Please try again.'),
        }),
    })
  }

  return (
    <div>
      {isError ? (
        // Never the empty state. "Nobody has claimed here" and "we could not ask" are different
        // facts, and the first is the one a front desk acts on — telling them the list is empty
        // when the request failed sends a member away who is standing at the counter.
        <div className="py-8 text-center">
          <p className="text-sm font-medium text-gray-700">We could not load your claim list.</p>
          <p className="mt-1 text-xs text-gray-500">
            This is a problem on our end, not a sign that nobody has claimed here.
          </p>
          <CMButton variant="ghost" className="mt-3" onClick={() => refetch()}>
            Try again
          </CMButton>
        </div>
      ) : claims.length === 0 ? (
        <EmptyState message="Members who claim a benefit at your club will appear here." />
      ) : (
        <div className="overflow-x-auto">
          {/* Above the table, because reconciling against a till is done from the file rather than
              from the screen — and someone who came here to do that should not have to scroll a
              long list to find out it is possible. */}
          <ClubClaimsExport clubId={clubId} />
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-2 pr-3 font-medium">Member</th>
                <th className="py-2 pr-3 font-medium">DUPR ID</th>
                {/* Beside the DUPR ID because they are the two things a member reads out, and the
                    two a staff member scans this table for. */}
                <th className="py-2 pr-3 font-medium">Claim ref</th>
                <th className="py-2 pr-3 font-medium">Benefit</th>
                <th className="py-2 pr-3 font-medium">Claimed</th>
                <th className="py-2 pr-3 font-medium">Redeemed</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim) => (
                <tr key={claim.grantId} className="border-b border-gray-100">
                  <td className="py-2 pr-3 font-medium text-gray-900">{claim.name || '—'}</td>
                  <td className="py-2 pr-3 text-gray-600">{claim.duprId ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono tabular-nums text-gray-600">{claim.claimReference || '—'}</td>
                  <td className="py-2 pr-3 text-gray-600">{claim.label ?? '—'}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-gray-500">
                    {moment(claim.claimedAt).format('D MMM YYYY')}
                  </td>
                  <td className="py-2 pr-3">
                    {claim.redeemedAt ? (
                      <span className="whitespace-nowrap text-gray-500">
                        {moment(claim.redeemedAt).format('D MMM YYYY')}
                      </span>
                    ) : (
                      <>
                        <button
                          className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                          disabled={markRedeemed.isLoading}
                          onClick={() => markClaimRedeemed(claim.grantId)}
                        >
                          Mark redeemed
                        </button>
                        {redeemError?.grantId === claim.grantId && (
                          <span className="mt-1 block text-[11px] text-red-600">{redeemError.message}</span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hasNextPage && (
            <button
              className="mt-3 w-full py-2 text-xs text-gray-600 underline disabled:opacity-40"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? 'Loading…' : 'Show earlier claims'}
            </button>
          )}
        </div>
      )}

      {/* A failed fetch is not an answer. The picker renders one rule row per offer, so an empty
          offer list makes it say "switch on a benefit first" — an untrue sentence about a club that
          may well offer plenty. Both fetches feed the one section, so one message covers both.
          Same shape as the claim list above. */}
      {offersFailed || templatesFailed ? (
        <SectionError
          title="We could not load who can claim at your club."
          detail="The rules you have already set are still in force — we just could not show them."
          onRetry={() => {
            if (offersFailed) refetchOffers()
            if (templatesFailed) refetchTemplates()
          }}
        />
      ) : (
        <BenefitEligibilityPicker clubId={clubId} offers={offers ?? []} />
      )}

      <p className="mt-3 text-xs text-gray-400">
        DUPR does not track whether a benefit was honored — this list is yours to manage.
      </p>
    </div>
  )
}

interface SectionErrorProps {
  title: string
  detail: string
  onRetry: () => void
}

/**
 * One section of this tab could not be loaded.
 *
 * Says so in place rather than leaving the section absent or empty: every section here renders a
 * plausible-looking answer from no data, and a director cannot tell that apart from the truth.
 */
const SectionError = ({ title, detail, onRetry }: SectionErrorProps) => (
  <div className="mt-6 border-t border-gray-100 pt-4">
    <p className="text-sm font-medium text-gray-700">{title}</p>
    <p className="mt-1 text-xs text-gray-500">{detail}</p>
    <CMButton variant="ghost" className="mt-2" onClick={onRetry}>
      Try again
    </CMButton>
  </div>
)
